import React, { useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, Image,
  StyleSheet, ScrollView, Platform, ActivityIndicator, Alert,
  KeyboardAvoidingView,
} from 'react-native';
import Slider from '@react-native-community/slider';
import * as ImagePicker from 'expo-image-picker';
import { useDispatch } from 'react-redux';
import { colors, fonts, RD } from '../../theme/colors';
import { LogoLockup } from '../../components/Logo';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { RadiusMap } from '../../components/RadiusMap';
import { VehicleAutocomplete } from '../../components/VehicleAutocomplete';
import { api } from '../../services/api';
import { setToken, setDriver } from '../../store';

const VEHICLE_TYPES = [
  { key: 'VAN',          label: 'Van',          icon: '🚐' },
  { key: 'PICKUP_TRUCK', label: 'Pickup Truck', icon: '🛻' },
  { key: 'BOX_TRUCK',    label: 'Box Truck',    icon: '📦' },
  { key: 'TRAILER',      label: 'Trailer',      icon: '🚚' },
];

const TOTAL_STEPS = 4;

function fmt(digits: string): string {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6,10)}`;
}

export function RegisterScreen({ navigation }: any) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);

  // Step 1
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [rawPhone, setRawPhone] = useState('');
  const [password, setPassword] = useState('');
  const [pwError, setPwError]   = useState('');

  // Step 2
  const [profilePhoto, setProfilePhoto]   = useState<string | null>(null);
  const [licensePhoto, setLicensePhoto]   = useState<string | null>(null);
  const [hasGL, setHasGL]                 = useState<boolean | null>(null);
  const [glInsurance, setGlInsurance]     = useState<string | null>(null);
  const [carInsurance, setCarInsurance]   = useState<string | null>(null);

  // Step 3
  const [vehicleType, setVehicleType]       = useState('VAN');
  const [vehicleDetails, setVehicleDetails] = useState('');
  const [insuranceProvider, setInsurance]   = useState('');

  // Step 4
  const [baseAddress, setBaseAddress]       = useState('');
  const [baseLat, setBaseLat]               = useState(40.7128);
  const [baseLng, setBaseLng]               = useState(-74.0060);
  const [workingRadius, setWorkingRadius]   = useState(200);
  const [addressSelected, setAddressSelected] = useState(false);

  const [loading, setLoading] = useState(false);
  /** "Uploading 2 of 4" while documents go up one at a time. A bare spinner is
   *  indistinguishable from a hang, which is what applicants were staring at. */
  const [uploadNote, setUploadNote] = useState('');
  const [showPw, setShowPw]   = useState(false);

  const validatePassword = (pw: string): string => {
    if (pw.length < 8)             return 'At least 8 characters required';
    if (!/[A-Z]/.test(pw))         return 'Must include at least 1 uppercase letter';
    if (!/[0-9]/.test(pw))         return 'Must include at least 1 number';
    if (!/[^A-Za-z0-9]/.test(pw))  return 'Must include at least 1 special character';
    return '';
  };

  const handlePhoneChange = (text: string) => {
    const digits = text.replace(/\D/g, '').slice(0, 10);
    setRawPhone(digits);
  };

  const pickImage = async (setter: (uri: string) => void) => {
    if (Platform.OS === 'web') {
      // Use native file input on web for reliable file access
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = () => {
        const file = input.files?.[0];
        if (file) {
          const url = URL.createObjectURL(file);
          // Store the File object for later FormData use
          (window as any).__cpFiles = (window as any).__cpFiles || {};
          (window as any).__cpFiles[url] = file;
          setter(url);
        }
      };
      input.click();
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.8,
    });
    if (!result.canceled && result.assets[0]) {
      setter(result.assets[0].uri);
    }
  };

  const [regError, setRegError] = useState('');

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  // Validate address has street number, street, city, state (full address from autocomplete)
  const isFullAddress = (addr: string) => {
    const parts = addr.split(',').map(p => p.trim());
    if (parts.length < 3) return false; // need at least "123 St, City, State ZIP"
    if (!/\d/.test(parts[0])) return false; // first part should have a number (street address)
    return true;
  };

  const handleRegister = async () => {
    if (!baseAddress.trim()) {
      showAlert('Required', 'Please enter your home address.');
      return;
    }
    if (!addressSelected || !isFullAddress(baseAddress)) {
      showAlert('Full Address Required', 'Please select a complete address from the dropdown suggestions (including street number, city, and state).');
      return;
    }
    setLoading(true);
    setRegError('');
    try {
      console.log('[Register] Submitting to API...', { name, email, phone: `+1${rawPhone}`, vehicleType, baseAddress, baseLat, baseLng });

      /**
       * 🚨 EACH DOCUMENT ON ITS OWN, BEFORE ANYTHING ELSE.
       *
       * Sign-up used to post the profile photo, licence and both insurance documents as
       * ONE multipart body. On LTE that body ends early, the server sees "Unexpected end
       * of form", and the applicant is told their photos did not finish uploading - so a
       * driver simply cannot create an account. Reported 2026-09-06 from a phone on LTE.
       *
       * Each document now gets its own presigned PUT with its own retry, so a dropped
       * connection costs one document instead of the whole sign-up. If signing is
       * unavailable we fall through to the original multipart post below, unchanged.
       */
      const fields = {
        profilePhotoUrl: '' as string,
        licensePhotoUrl: '' as string,
        carInsuranceUrl: '' as string,
        insuranceDocUrl: '' as string,
      };
      let usedDirectUpload = false;
      try {
        const { uploadDriverDocs } = await import('../../services/mediaUpload');
        const total = [profilePhoto, licensePhoto, carInsurance, glInsurance].filter(Boolean).length;
        if (total > 0) {
          setUploadNote(`Uploading 0 of ${total}...`);
          const up = await uploadDriverDocs(
            [
              { field: 'profilePhotoUrl', uri: profilePhoto || null },
              { field: 'licensePhotoUrl', uri: licensePhoto || null },
              { field: 'carInsuranceUrl', uri: carInsurance || null },
              { field: 'insuranceDocUrl', uri: glInsurance || null },
            ],
            (done, n) => setUploadNote(`Uploading ${done} of ${n}...`),
          );
          setUploadNote('');
          if (!up.signingUnavailable) {
            usedDirectUpload = true;
            Object.assign(fields, up.urls);
            // The profile photo is the only REQUIRED document, so it is the only failure
            // that has to stop the sign-up. Anything else missing is named and the
            // applicant is let through - an admin can chase one document later, and
            // blocking the whole account over the second insurance form helps nobody.
            if (profilePhoto && !fields.profilePhotoUrl) {
              setLoading(false);
              showAlert('Photo did not upload', 'Your profile photo did not reach us, so we could not finish the sign-up. This is our side, not yours. Please try again on a stronger signal.');
              return;
            }
          }
        }
      } catch (e: any) {
        setUploadNote('');
        console.warn('[Register] per-file upload failed, falling back to multipart:', e?.message || e);
      }

      // Build FormData so files (license, insurance, profile photo) are uploaded
      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('password', password);
      formData.append('phone', `+1${rawPhone}`);
      formData.append('vehicleType', vehicleType);
      formData.append('vehicleDetails', vehicleDetails);
      formData.append('insuranceProvider', insuranceProvider);
      formData.append('baseLat', String(baseLat));
      formData.append('baseLng', String(baseLng));
      formData.append('baseAddress', baseAddress);
      formData.append('workingRadius', String(workingRadius));
      formData.append('tosAccepted', 'true');

      // Helper: append file to FormData
      const appendFile = async (fieldName: string, uri: string, fileName: string) => {
        if (Platform.OS === 'web') {
          // Try to use the stored File object from native file input
          const storedFile = (window as any).__cpFiles?.[uri];
          if (storedFile instanceof File) {
            console.log(`[Register] Using stored File for ${fieldName}: ${storedFile.name} (${storedFile.size} bytes)`);
            formData.append(fieldName, storedFile, fileName);
          } else {
            // Fallback: fetch the blob URI
            try {
              const response = await fetch(uri);
              const blob = await response.blob();
              const ext = fileName.split('.').pop() || 'jpg';
              const file = new File([blob], fileName, { type: blob.type || `image/${ext}` });
              console.log(`[Register] Fetched blob for ${fieldName}: ${file.size} bytes, type=${file.type}`);
              formData.append(fieldName, file);
            } catch (e) {
              console.error(`[Register] FAILED to get file for ${fieldName}:`, e);
            }
          }
        } else {
          const ext = uri.split('.').pop() || 'jpg';
          formData.append(fieldName, { uri, name: fileName, type: `image/${ext}` } as any);
        }
      };

      // Already uploaded? Send the links and keep the body small. Only fall back to
      // shipping the bytes when the direct upload could not run at all.
      for (const [k, v] of Object.entries(fields)) if (v) formData.append(k, v);
      if (!usedDirectUpload) {
        if (profilePhoto) await appendFile('profilePhoto', profilePhoto, 'profile.jpg');
        if (licensePhoto) await appendFile('licensePhoto', licensePhoto, 'license.jpg');
        if (carInsurance) await appendFile('carInsurance', carInsurance, 'car-insurance.jpg');
        if (glInsurance) await appendFile('insuranceDoc', glInsurance, 'gl-insurance.jpg');
      }

      // Debug: log FormData entries on web
      if (Platform.OS === 'web' && typeof (formData as any).entries === 'function') {
        for (const [key, val] of (formData as any).entries()) {
          if (val instanceof File) {
            console.log(`[Register] FormData: ${key} = File(${val.name}, ${val.size} bytes, ${val.type})`);
          } else if (val instanceof Blob) {
            console.log(`[Register] FormData: ${key} = Blob(${val.size} bytes, ${val.type})`);
          } else {
            console.log(`[Register] FormData: ${key} = ${typeof val === 'string' ? val.slice(0, 50) : val}`);
          }
        }
      }

      let data: any;
      if (Platform.OS === 'web') {
        // Use native fetch on web — axios misidentifies FormData and sends as JSON
        const baseUrl = (api.defaults as any).baseURL || '';
        console.log('[Register] Using native fetch to', `${baseUrl}/auth/register`);
        const resp = await fetch(`${baseUrl}/auth/register`, {
          method: 'POST',
          body: formData,
          // Do NOT set Content-Type — browser adds multipart boundary automatically
        });
        data = await resp.json();
        if (!resp.ok) throw { response: { status: resp.status, data } };
      } else {
        const resp = await api.post('/auth/register', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        data = resp.data;
      }
      console.log('[Register] Success:', data.driver?.id);
      dispatch(setToken(data.token));
      dispatch(setDriver(data.driver));
      // Do NOT navigation.replace('Main') here: 'Main' only exists in the authenticated
      // stack, which hasn't mounted yet this tick. Setting the token swaps the stack on the
      // next render (same pattern LoginScreen uses) — an explicit replace throws
      // "REPLACE not handled by any navigator" and crashes registration.
    } catch (err: any) {
      console.error('[Register] Error:', err?.message, err?.response?.status, err?.response?.data);
      const msg = err?.response?.data?.error || err?.message || 'Unable to connect. Please check your connection and try again.';
      setRegError(msg);
      showAlert('Registration Failed', msg);
    } finally {
      setLoading(false);
    }
  };

  // Clear vehicle details when type changes
  const handleVehicleTypeChange = (type: string) => {
    setVehicleType(type);
    setVehicleDetails('');
  };

  const step1Valid = name.trim() && email.trim() && rawPhone.length === 10 && !validatePassword(password);
  const step2Valid = profilePhoto && licensePhoto && hasGL !== null;
  const step3Valid = vehicleDetails.trim() && (insuranceProvider.trim() || carInsurance);

  return (
    <KeyboardAvoidingView
      style={styles.scrollView}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.page}>
        {/* ── Top bar ──────────────────────────────────── */}
        <View style={styles.topBar}>
          <TouchableOpacity
            style={styles.backBtn}
            onPress={() => step > 1 ? setStep(step - 1) : navigation.goBack()}
          >
            <Text style={styles.backArrow}>{'‹'}</Text>
            <Text style={styles.backLabel}>Back</Text>
          </TouchableOpacity>
          <View style={styles.topBarLogoWrap}>
            <LogoLockup />
          </View>
          <View style={styles.topBarSpacer} />
        </View>

        {/* ── Card container ───────────────────────────── */}
        <View style={styles.card}>
          {/* Header */}
          <View style={styles.cardHeader}>
            <Text style={styles.heading}>Create your account</Text>
            <Text style={styles.subheading}>
              {step === 1 && 'Tell us about yourself to get started.'}
              {step === 2 && 'Upload your documents for verification.'}
              {step === 3 && 'Tell us about your vehicle.'}
              {step === 4 && 'Set your home base and working radius.'}
            </Text>
          </View>

          {/* Progress */}
          <View style={styles.progressSection}>
            <View style={styles.progressInfo}>
              <Text style={styles.stepIndicator}>Step {step} of {TOTAL_STEPS}</Text>
              <Text style={styles.stepPercent}>{Math.round((step / TOTAL_STEPS) * 100)}%</Text>
            </View>
            <View style={styles.progressTrack}>
              <View style={[styles.progressFill, { width: `${(step / TOTAL_STEPS) * 100}%` as any }]} />
            </View>
          </View>

          {/* ── Step 1: Personal Info ──────────────────── */}
          {step === 1 && (
            <View style={styles.stepContent}>
              <View style={styles.fieldGrid}>
                <Field label="Full Name" required>
                  <TextInput
                    style={styles.input}
                    placeholder="Jane Smith"
                    value={name}
                    onChangeText={setName}
                    autoCapitalize="words"
                    autoComplete="off"
                    placeholderTextColor={RD.textFaint}
                  />
                </Field>

                <Field label="Email Address" required>
                  <TextInput
                    style={styles.input}
                    placeholder="jane@email.com"
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="off"
                    placeholderTextColor={RD.textFaint}
                  />
                </Field>
              </View>

              <Field label="Phone Number" required>
                <View style={styles.phoneRow}>
                  <View style={styles.phonePrefix}>
                    <Text style={styles.phonePrefixText}>+1</Text>
                  </View>
                  <TextInput
                    style={styles.phoneInput}
                    placeholder="(555) 000-1234"
                    value={fmt(rawPhone)}
                    onChangeText={handlePhoneChange}
                    keyboardType="phone-pad"
                    autoComplete="off"
                    autoCorrect={false}
                    placeholderTextColor={RD.textFaint}
                  />
                </View>
              </Field>

              <Field label="Password" required>
                <View style={styles.pwRow}>
                  <TextInput
                    style={styles.pwInput}
                    placeholder="Min. 8 characters"
                    value={password}
                    onChangeText={v => { setPassword(v); setPwError(''); }}
                    secureTextEntry={!showPw}
                    autoComplete="new-password"
                    placeholderTextColor={RD.textFaint}
                  />
                  <TouchableOpacity onPress={() => setShowPw(!showPw)} style={styles.showBtn}>
                    <Text style={styles.showBtnText}>{showPw ? 'Hide' : 'Show'}</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.pwReqs}>
                  <PwReq met={password.length >= 8}          label="8+ characters" />
                  <PwReq met={/[A-Z]/.test(password)}        label="1 uppercase" />
                  <PwReq met={/[0-9]/.test(password)}        label="1 number" />
                  <PwReq met={/[^A-Za-z0-9]/.test(password)} label="1 special character" />
                </View>
                {!!pwError && <Text style={styles.errorText}>{pwError}</Text>}
              </Field>

              <View style={styles.actions}>
                <TouchableOpacity
                  style={[styles.primaryBtn, !step1Valid && styles.primaryBtnDisabled]}
                  onPress={() => {
                    const err = validatePassword(password);
                    if (err) { setPwError(err); return; }
                    setPwError('');
                    setStep(2);
                  }}
                  disabled={!step1Valid}
                >
                  <Text style={styles.primaryBtnText}>Continue</Text>
                  <Text style={styles.primaryBtnArrow}>{'›'}</Text>
                </TouchableOpacity>

                <TouchableOpacity onPress={() => navigation.navigate('Login')} style={styles.secondaryAction}>
                  <Text style={styles.secondaryText}>
                    Already have an account?{' '}
                    <Text style={styles.secondaryLink}>Log in</Text>
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* ── Step 2: Documents ──────────────────────── */}
          {step === 2 && (
            <View style={styles.stepContent}>
              <View style={styles.infoBox}>
                <Text style={styles.infoBoxIcon}>i</Text>
                <Text style={styles.infoBoxText}>
                  We need to verify your identity and insurance before you can start accepting deliveries.
                </Text>
              </View>

              <Field label="Profile Photo" required hint="Helps customers recognize you">
                <PhotoUpload
                  uri={profilePhoto}
                  placeholder="Upload profile photo"
                  onPress={() => pickImage(setProfilePhoto)}
                  required
                />
              </Field>

              <View style={styles.fieldGrid}>
                <Field label="Driver's License" required hint="Front of your valid license">
                  <PhotoUpload
                    uri={licensePhoto}
                    placeholder="Upload license"
                    onPress={() => pickImage(setLicensePhoto)}
                    required
                  />
                </Field>

                <Field label="Car Insurance" hint="Current auto insurance card (optional)">
                  <PhotoUpload
                    uri={carInsurance}
                    placeholder="Upload insurance"
                    onPress={() => pickImage(setCarInsurance)}
                  />
                </Field>
              </View>

              <Field label="General Liability Insurance" hint="Do you currently have GL coverage?">
                <View style={styles.toggleRow}>
                  <TouchableOpacity
                    style={[styles.toggleBtn, hasGL === true && styles.toggleBtnActive]}
                    onPress={() => setHasGL(true)}
                  >
                    <Text style={[styles.toggleBtnText, hasGL === true && styles.toggleBtnTextActive]}>Yes, I have GL</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.toggleBtn, hasGL === false && styles.toggleBtnActive]}
                    onPress={() => { setHasGL(false); setGlInsurance(null); }}
                  >
                    <Text style={[styles.toggleBtnText, hasGL === false && styles.toggleBtnTextActive]}>No</Text>
                  </TouchableOpacity>
                </View>

                {hasGL === true && (
                  <View style={{ marginTop: 12 }}>
                    <PhotoUpload
                      uri={glInsurance}
                      placeholder="Upload GL insurance document"
                      onPress={() => pickImage(setGlInsurance)}
                    />
                  </View>
                )}
                {hasGL === false && (
                  <View style={styles.glNote}>
                    <Text style={styles.glNoteText}>No worries — you can add this later from your profile settings.</Text>
                  </View>
                )}
              </Field>

              <View style={styles.actions}>
                <TouchableOpacity
                  style={[styles.primaryBtn, !step2Valid && styles.primaryBtnDisabled]}
                  disabled={!step2Valid}
                  onPress={() => {
                    if (!profilePhoto) {
                      showAlert('Required', 'Please upload your profile photo.');
                      return;
                    }
                    if (!licensePhoto) {
                      showAlert('Required', "Please upload your driver's license.");
                      return;
                    }
                    setStep(3);
                  }}
                >
                  <Text style={styles.primaryBtnText}>Continue</Text>
                  <Text style={styles.primaryBtnArrow}>{'›'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* ── Step 3: Vehicle Info ───────────────────── */}
          {step === 3 && (
            <View style={styles.stepContent}>
              <Field label="Vehicle Type" required>
                <View style={styles.vehicleTypeGrid}>
                  {VEHICLE_TYPES.map(v => (
                    <TouchableOpacity
                      key={v.key}
                      style={[
                        styles.vehicleTypeCard,
                        vehicleType === v.key && styles.vehicleTypeCardActive,
                      ]}
                      onPress={() => handleVehicleTypeChange(v.key)}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.vehicleTypeIcon}>{v.icon}</Text>
                      <Text style={[
                        styles.vehicleTypeLabel,
                        vehicleType === v.key && styles.vehicleTypeLabelActive,
                      ]}>
                        {v.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </Field>

              <View style={{ zIndex: 999 }}>
                <Field label="Vehicle Make & Model" required hint="Start typing to search">
                  <VehicleAutocomplete
                    value={vehicleDetails}
                    onChangeText={setVehicleDetails}
                    vehicleType={vehicleType}
                  />
                </Field>
              </View>

              <View style={{ zIndex: 1 }}>
                <Field label="Insurance Provider" required>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. Progressive, State Farm, GEICO"
                  value={insuranceProvider}
                  onChangeText={setInsurance}
                  autoComplete="off"
                  placeholderTextColor={RD.textFaint}
                />
              </Field>
              </View>

              <View style={styles.actions}>
                <TouchableOpacity
                  style={[styles.primaryBtn, !step3Valid && styles.primaryBtnDisabled]}
                  disabled={!step3Valid}
                  onPress={() => setStep(4)}
                >
                  <Text style={styles.primaryBtnText}>Continue</Text>
                  <Text style={styles.primaryBtnArrow}>{'›'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* ── Step 4: Home Base ──────────────────────── */}
          {step === 4 && (
            <View style={styles.stepContent}>
              <View style={styles.infoBox}>
                <Text style={styles.infoBoxIcon}>i</Text>
                <Text style={styles.infoBoxText}>
                  Set your home base and how far you're willing to travel. You can change this anytime.
                </Text>
              </View>

              <View style={{ zIndex: 999 }}>
                <Field label="Home Base Address" required hint="Start typing to search">
                  <AddressAutocomplete
                    value={baseAddress}
                    placeholder="Search your address..."
                    onSelect={(address, lat, lng) => {
                      setBaseAddress(address);
                      setBaseLat(lat);
                      setBaseLng(lng);
                      setAddressSelected(true);
                    }}
                    onError={(msg) => {
                      setAddressSelected(false);
                      showAlert('Address Error', msg);
                    }}
                  />
                </Field>
              </View>

              {baseAddress.length > 0 && (
                <View style={styles.mapContainer}>
                  <RadiusMap
                    latitude={baseLat}
                    longitude={baseLng}
                    radiusMiles={workingRadius}
                    height={300}
                  />
                </View>
              )}

              <Field
                label={`Working Radius`}
                hint={`You'll see jobs within ${workingRadius} miles of your home base`}
              >
                <View style={styles.sliderContainer}>
                  <View style={styles.sliderValueRow}>
                    <Text style={styles.sliderValue}>{workingRadius}</Text>
                    <Text style={styles.sliderUnit}>miles</Text>
                  </View>
                  <Slider
                    style={styles.slider}
                    minimumValue={10}
                    maximumValue={1000}
                    step={10}
                    value={workingRadius}
                    onValueChange={setWorkingRadius}
                    minimumTrackTintColor={RD.maroon}
                    maximumTrackTintColor={RD.g15}
                    thumbTintColor={RD.maroon}
                  />
                  <View style={styles.sliderLabels}>
                    <Text style={styles.sliderLabel}>10 mi</Text>
                    <Text style={styles.sliderLabel}>1,000 mi</Text>
                  </View>
                </View>
              </Field>

              <View style={styles.tosSection}>
                <Text style={styles.tosText}>
                  By creating an account you agree to Commonplace's{' '}
                  <Text style={styles.tosLink}>Terms of Service</Text> and{' '}
                  <Text style={styles.tosLink}>Privacy Policy</Text>.
                </Text>
              </View>

              {regError ? (
                <View style={styles.errorBox}>
                  <Text style={styles.errorText}>{regError}</Text>
                </View>
              ) : null}

              <View style={styles.actions}>
                <TouchableOpacity
                  style={[styles.primaryBtn, (loading || !addressSelected || !baseAddress.trim()) && styles.primaryBtnDisabled]}
                  onPress={handleRegister}
                  disabled={loading || !addressSelected || !baseAddress.trim()}
                >
                  {loading ? (
                    /* Say what it is doing and how far along. A spinner with no number
                       reads as a hang, which is exactly the complaint. */
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <ActivityIndicator color={colors.white} />
                      {uploadNote ? <Text style={styles.primaryBtnText}>{uploadNote}</Text> : null}
                    </View>
                  ) : (
                    <>
                      <Text style={styles.primaryBtnText}>Create Account</Text>
                      <Text style={styles.primaryBtnArrow}>{'›'}</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </ScrollView>
    </KeyboardAvoidingView>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PwReq({ met, label }: { met: boolean; label: string }) {
  return (
    <View style={pwStyles.row}>
      <View style={[pwStyles.indicator, met && pwStyles.indicatorMet]}>
        {met && <Text style={pwStyles.check}>{'✓'}</Text>}
      </View>
      <Text style={[pwStyles.text, met && pwStyles.textMet]}>{label}</Text>
    </View>
  );
}
const pwStyles = StyleSheet.create({
  row:          { flexDirection: 'row', alignItems: 'center', gap: 8 },
  indicator:    { width: 18, height: 18, borderRadius: 9, borderWidth: 1.5, borderColor: RD.borderStrong, justifyContent: 'center', alignItems: 'center', backgroundColor: RD.surface },
  indicatorMet: { borderColor: RD.money, backgroundColor: RD.money },
  check:        { fontSize: 11, color: '#fff', fontWeight: '700' },
  text:         { fontSize: 13, color: RD.textFaint, fontFamily: fonts.regular },
  textMet:      { color: RD.textSec },
});

function Field({ label, hint, required, children }: {
  label: string; hint?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <View style={styles.field}>
      <View style={styles.fieldLabelRow}>
        <Text style={styles.fieldLabel}>{label}</Text>
        {required && <Text style={styles.fieldRequired}>*</Text>}
      </View>
      {hint && <Text style={styles.fieldHint}>{hint}</Text>}
      {children}
    </View>
  );
}

function PhotoUpload({ uri, placeholder, onPress, required }: {
  uri: string | null; placeholder: string; onPress: () => void; required?: boolean;
}) {
  return (
    <TouchableOpacity
      style={[styles.uploadBox, uri ? styles.uploadBoxFilled : null]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {uri ? (
        <Image source={{ uri }} style={styles.uploadPreview} />
      ) : (
        <View style={styles.uploadPlaceholder}>
          <View style={styles.uploadIconCircle}>
            <Text style={styles.uploadIconText}>+</Text>
          </View>
          <Text style={styles.uploadLabel}>{placeholder}</Text>
          {required && <Text style={styles.uploadRequired}>Required</Text>}
        </View>
      )}
    </TouchableOpacity>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const MAX_WIDTH = 560;

const styles = StyleSheet.create({
  errorBox: {
    backgroundColor: RD.dangerSoft,
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: RD.danger,
  },
  scrollView: {
    flex: 1,
    backgroundColor: RD.bg,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 16,
  },
  page: {
    width: '100%',
    maxWidth: MAX_WIDTH,
  },

  // Top bar
  topBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  topBarLogoWrap: {
    flex: 1,
    alignItems: 'center',
  },
  topBarSpacer: {
    width: 70,  // matches approximate back button width for balance
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingRight: 12,
  },
  backArrow: {
    fontSize: 24,
    color: RD.textSec,
    fontWeight: '300',
    marginTop: -2,
  },
  backLabel: {
    fontSize: 15,
    color: RD.textSec,
    fontFamily: fonts.medium,
  },

  // Card
  card: {
    backgroundColor: RD.surface,
    borderRadius: 18,
    padding: 24,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
    borderWidth: 1,
    borderColor: RD.border,
  },
  cardHeader: {
    marginBottom: 16,
  },
  heading: {
    fontSize: 22,
    fontFamily: fonts.heavy,
    color: RD.text,
    marginBottom: 4,
  },
  subheading: {
    fontSize: 15,
    fontFamily: fonts.regular,
    color: RD.textMuted,
    lineHeight: 22,
  },

  // Progress
  progressSection: {
    marginBottom: 18,
  },
  progressInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  stepIndicator: {
    fontSize: 13,
    fontFamily: fonts.semiBold,
    color: RD.textSec,
  },
  stepPercent: {
    fontSize: 13,
    fontFamily: fonts.medium,
    color: RD.textFaint,
  },
  progressTrack: {
    height: 4,
    backgroundColor: RD.g07,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: 4,
    backgroundColor: RD.maroon,
    borderRadius: 2,
  },

  // Step content
  stepContent: {
    gap: 16,
  },

  // Fields
  field: {
    gap: 6,
    zIndex: 1,
  },
  fieldLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  fieldLabel: {
    fontSize: 14,
    fontFamily: fonts.semiBold,
    color: RD.text,
  },
  fieldRequired: {
    fontSize: 14,
    color: RD.danger,
    fontFamily: fonts.semiBold,
  },
  fieldHint: {
    fontSize: 13,
    fontFamily: fonts.regular,
    color: RD.textFaint,
    marginTop: -2,
  },
  fieldGrid: {
    flexDirection: 'column',
    gap: 18,
  },

  // Input
  input: {
    backgroundColor: RD.surface,
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
    color: RD.text,
    fontFamily: fonts.regular,
  },

  // Password row
  pwRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: RD.surface,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
  },
  pwInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
    color: RD.text,
    fontFamily: fonts.regular,
  },
  showBtn: {
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  showBtnText: {
    fontSize: 13,
    color: RD.textMuted,
    fontFamily: fonts.semiBold,
  },

  // Phone
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: RD.surface,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
    overflow: 'hidden',
  },
  phonePrefix: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRightWidth: 1.5,
    borderRightColor: RD.border,
    backgroundColor: RD.g03,
  },
  phonePrefixText: {
    fontSize: 15,
    fontFamily: fonts.semiBold,
    color: RD.text,
  },
  phoneInput: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: RD.text,
    fontFamily: fonts.regular,
  },

  // Password requirements
  pwReqs: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    paddingTop: 10,
  },

  // Error
  errorText: {
    fontSize: 13,
    color: RD.danger,
    fontFamily: fonts.medium,
    marginTop: 2,
  },

  // Info box
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    backgroundColor: RD.maroonSoft,
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: RD.maroon + '40',
  },
  infoBoxIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: RD.maroon,
    color: '#fff',
    fontSize: 13,
    fontFamily: fonts.semiBold,
    textAlign: 'center',
    lineHeight: 22,
    overflow: 'hidden',
  },
  infoBoxText: {
    flex: 1,
    fontSize: 14,
    color: RD.textSec,
    fontFamily: fonts.regular,
    lineHeight: 21,
  },

  // Upload
  uploadBox: {
    borderWidth: 2,
    borderColor: RD.borderStrong,
    borderRadius: 14,
    borderStyle: 'dashed',
    overflow: 'hidden',
    backgroundColor: RD.g03,
  },
  uploadBoxFilled: {
    borderStyle: 'solid',
    borderColor: RD.money,
    backgroundColor: RD.surface,
  },
  uploadPlaceholder: {
    paddingVertical: 28,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  uploadIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: RD.surface,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadIconText: {
    fontSize: 20,
    color: RD.textFaint,
    fontWeight: '300',
    marginTop: -1,
  },
  uploadLabel: {
    fontSize: 14,
    color: RD.textFaint,
    fontFamily: fonts.medium,
  },
  uploadRequired: {
    fontSize: 11,
    color: RD.maroon,
    fontFamily: fonts.semiBold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  uploadPreview: {
    width: '100%',
    height: 140,
    resizeMode: 'cover',
  },

  // Toggle (GL)
  toggleRow: {
    flexDirection: 'row',
    gap: 12,
  },
  toggleBtn: {
    flex: 1,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: 'center',
    backgroundColor: RD.surface,
  },
  toggleBtnActive: {
    borderColor: RD.maroon,
    backgroundColor: RD.maroonSoft,
  },
  toggleBtnText: {
    fontSize: 15,
    color: RD.textMuted,
    fontFamily: fonts.medium,
  },
  toggleBtnTextActive: {
    color: RD.maroon,
    fontFamily: fonts.semiBold,
  },
  glNote: {
    marginTop: 10,
    backgroundColor: RD.g03,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: RD.border,
  },
  glNoteText: {
    fontSize: 13,
    color: RD.textSec,
    fontFamily: fonts.regular,
  },

  // Vehicle type selector
  vehicleTypeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  vehicleTypeCard: {
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
    gap: 4,
    backgroundColor: RD.surface,
    minWidth: 90,
  },
  vehicleTypeCardActive: {
    borderColor: RD.maroon,
    backgroundColor: RD.maroonSoft,
  },
  vehicleTypeIcon: {
    fontSize: 20,
  },
  vehicleTypeLabel: {
    fontSize: 12,
    color: RD.textSec,
    fontFamily: fonts.medium,
    textAlign: 'center',
  },
  vehicleTypeLabelActive: {
    color: RD.maroon,
    fontFamily: fonts.semiBold,
  },

  // Map
  mapContainer: {
    borderRadius: 16,
    overflow: 'hidden',
  },

  // Slider
  sliderContainer: {
    gap: 2,
  },
  sliderValueRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 4,
  },
  sliderValue: {
    fontSize: 32,
    fontFamily: fonts.heavy,
    color: RD.maroon,
  },
  sliderUnit: {
    fontSize: 15,
    fontFamily: fonts.medium,
    color: RD.textFaint,
  },
  slider: {
    width: '100%',
    height: 44,
  },
  sliderLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
  },
  sliderLabel: {
    fontSize: 12,
    color: RD.textFaint,
    fontFamily: fonts.regular,
  },

  // ToS
  tosSection: {
    paddingHorizontal: 4,
  },
  tosText: {
    fontSize: 13,
    color: RD.textFaint,
    fontFamily: fonts.regular,
    lineHeight: 20,
    textAlign: 'center',
  },
  tosLink: {
    color: RD.maroon,
    fontFamily: fonts.semiBold,
  },

  // Actions
  actions: {
    gap: 14,
    marginTop: 4,
  },
  primaryBtn: {
    backgroundColor: RD.ink,
    borderRadius: 16,
    height: 54,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
  },
  primaryBtnDisabled: {
    backgroundColor: RD.g30,
  },
  primaryBtnText: {
    color: '#fff',
    fontFamily: fonts.semiBold,
    fontSize: 16,
    fontWeight: '600',
  },
  primaryBtnArrow: {
    color: '#fff',
    fontSize: 22,
    fontWeight: '300',
    marginTop: -1,
  },
  secondaryAction: {
    alignItems: 'center',
    paddingVertical: 4,
  },
  secondaryText: {
    fontSize: 14,
    color: RD.textMuted,
    fontFamily: fonts.regular,
  },
  secondaryLink: {
    color: RD.maroon,
    fontFamily: fonts.semiBold,
  },
});
